import { Component, inject, signal } from '@angular/core'
import { form, FormField, FormRoot, validateStandardSchema } from '@angular/forms/signals'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { AuthApiService } from '../../api'
import { FieldErrorDirective } from '../../directives/field-error.directive'
import { LoadingButtonDirective } from '../../directives/loading-button.directive'
import { changePasswordFormModel, emptyChangePasswordForm, toChangePasswordBody } from '../../forms/auth-form.model'
import { describeApiError } from '../../i18n/describe-error'
import { VALIDATION_SK } from '../../i18n/validation.sk'
import { ACCOUNT_HOST } from './account-host'

/**
 * Zmeniť heslo, opened from Môj profil (docs/account.md). The request runs inside the dialog, so a wrong current
 * password is shown here and nothing typed is lost. Closes with `true` once the password is changed; this session
 * keeps going on the fresh token, every other one ends.
 */
@Component({
  selector: 'seiri-change-password-dialog',
  imports: [
    FieldErrorDirective,
    FormField,
    FormRoot,
    LoadingButtonDirective,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>Zmeniť heslo</h2>
      <button type="button" mat-icon-button mat-dialog-close aria-label="Zavrieť"><mat-icon>close</mat-icon></button>
    </div>
    <form [formRoot]="form">
      <mat-dialog-content class="dialog-body">
        <p>Po zmene hesla vás odhlásime na všetkých ostatných zariadeniach.</p>
        <mat-form-field appearance="outline">
          <mat-label>Súčasné heslo</mat-label>
          <input matInput type="password" autocomplete="current-password" [formField]="form.currentPassword" />
          <mat-error [seiriFieldError]="form.currentPassword" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Nové heslo</mat-label>
          <input matInput type="password" autocomplete="new-password" [formField]="form.password" />
          <mat-hint>{{ passwordRule }}</mat-hint>
          <mat-error [seiriFieldError]="form.password" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Nové heslo znova</mat-label>
          <input matInput type="password" autocomplete="new-password" [formField]="form.password2" />
          <mat-error [seiriFieldError]="form.password2" />
        </mat-form-field>

        @if (error()) {
          <div class="callout callout--danger" role="alert">
            <mat-icon aria-hidden="true">error_outline</mat-icon><span>{{ error() }}</span>
          </div>
        }
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button type="button" mat-button mat-dialog-close>Zrušiť</button>
        <button type="submit" mat-raised-button color="primary" [appLoadingButton]="form().submitting()">
          Zmeniť heslo
        </button>
      </mat-dialog-actions>
    </form>
  `,
})
export class ChangePasswordDialogComponent {
  private readonly host = inject(ACCOUNT_HOST)
  private readonly authApi = inject(AuthApiService)
  private readonly dialogRef = inject<MatDialogRef<ChangePasswordDialogComponent, boolean>>(MatDialogRef)

  protected readonly passwordRule = VALIDATION_SK['validation.passwordStrength']
  protected readonly error = signal('')
  protected readonly model = signal(emptyChangePasswordForm())
  protected readonly form = form(this.model, (path) => validateStandardSchema(path, changePasswordFormModel), {
    submission: {
      action: async (field) => {
        this.error.set('')
        try {
          const { accessToken } = await this.authApi.changePassword(toChangePasswordBody(field().value()))
          this.host.setToken(accessToken)
          this.dialogRef.close(true)
        } catch (error) {
          this.error.set(describeApiError(error, 'Heslo sa nepodarilo zmeniť'))
        }
        return undefined
      },
    },
  })
}
